import {
  Button,
  Stack,
  InputLabel,
  OutlinedInput,
  Typography,
  FormControl,
  Switch,
  Autocomplete,
  TextField,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import Modal, { CommonModalProps } from "..";
import { useState, forwardRef } from "react";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import ClearIcon from "@mui/icons-material/Clear";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { cacheService, orderService } from "../../../Core/Api/services";
import { DbMetadataViewFilter } from "../../../Core/Api/Types/db-metadata-view-filter";
import { produce } from "immer";
import { DbDeliveryProductInfos } from "../../../Core/Api/Types/db-delivery-product-infos";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import { useTranslation } from "react-i18next";
import FormSection from "../../Forms/FormSection/form-section";
import { DbOrderProductInfos } from "../../../Core/Api/Types/db-order-product-infos";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { UserHasAcl } from "../../../Utils/helpers";

export type ModalEditProductElementProps = {
  product: DbDeliveryProductInfos;
  groups: GroupAdvancedInfos[];
};

export type ModalEditProductProps = CommonModalProps<ModalEditProductElementProps>;

export function isOrderProduct(product: DbDeliveryProductInfos | DbOrderProductInfos): product is DbOrderProductInfos {
  return (product as any).proxy !== null && (product as any).proxy !== undefined;
}

const ModalEditProduct = forwardRef<any, ModalEditProductProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, product, groups } = props;

  const [blockedGroups, setBlockedGroups] = useState<GroupAdvancedInfos[]>(
    product.blockGroupIds && product.blockGroupIds.length > 0
      ? groups.filter((x) => product.blockGroupIds?.includes(x.id))
      : []
  );
  const [editProduct, setEditProduct] = useState<DbDeliveryProductInfos | DbOrderProductInfos>(product);
  const [metadataFilters, setMetadataFilters] = useState<DbMetadataViewFilter[]>(product.metadataFilters || []);

  const metadataFromStorage: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const isSuperUser = UserHasAcl(AclTypes.aclSuperUser) || UserHasAcl(AclTypes.aclAdmin);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleCancelClick = () => {
    closeModal();
  };

  const handleEditProduct = (e: any) => {
    setEditProduct((x) => ({
      ...x,
      [e.target.id]: typeof x[e.target.id] === "boolean" ? e.target.checked : e.target.value,
    }));
  };

  const handleConfirmClick = () => {
    let updatedProduct = editProduct;
    updatedProduct.blockGroupIds = blockedGroups.map((x) => x.id);
    updatedProduct.metadataFilters = metadataFilters.filter((x) => x.value !== null);

    orderService.UpdateProduct(updatedProduct).then((response) => {
      if (response.success) enqueueCustomSnackbar(t("alert:success.productUpdated"), "success");
      else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
    closeModal();
  };

  const updateMetaFilters = (value) => {
    let newMetaFilter: DbMetadataViewFilter = {
      fieldId: value.id,
      value: null,
    };
    if (metadataFilters === null) {
      setMetadataFilters([newMetaFilter]);
    } else {
      if (!metadataFilters.map((x) => x.fieldId).includes(value.id))
        setMetadataFilters([...metadataFilters, newMetaFilter]);
    }
  };

  return (
    <Modal
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
      title={t("modal:title.productUpdate")}
      reference={ref}
      closeModal={handleCancelClick}
      width={1200}
    >
      <Stack spacing={1}>
        <FormSection defaultExpanded={isSuperUser} size="small" title={t("common:general")}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl sx={{ width: "100%", mb: 0.5 }}>
                <InputLabel shrink>{t("common:name")}</InputLabel>
                <OutlinedInput
                  disabled={!isSuperUser}
                  fullWidth
                  id="name"
                  value={editProduct.name || ""}
                  onChange={handleEditProduct}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl sx={{ width: "100%", mb: 0.5 }}>
                <InputLabel shrink>{t("common:description")}</InputLabel>
                <OutlinedInput
                  fullWidth
                  disabled={!isSuperUser}
                  id="description"
                  value={editProduct.description || ""}
                  onChange={handleEditProduct}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.productType")}</InputLabel>
                <Autocomplete
                  disabled={!isSuperUser}
                  value={editProduct.type ?? ""}
                  onChange={(e, v) => handleEditProduct({ target: { value: v, id: "type" } })}
                  options={Object.keys(OrderProductType)}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%", mb: 0.5 }}>
                <InputLabel shrink>{t("field:label.isEnabled")}</InputLabel>
                <Switch
                  disabled={!isSuperUser}
                  id="enabled"
                  sx={{ mt: 2.5 }}
                  checked={editProduct.enabled ?? false}
                  onChange={handleEditProduct}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%", mb: 0.5 }}>
                <InputLabel shrink>{t("field:label.enabledAdminOnly")}</InputLabel>
                <Switch
                  disabled={!isSuperUser}
                  id="enabledAdminOnly"
                  sx={{ mt: 2.5 }}
                  checked={editProduct.enabledAdminOnly ?? false}
                  onChange={handleEditProduct}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%", mb: 0.5 }}>
                <InputLabel shrink>{t("field:label.canBeScheduled")}</InputLabel>
                <Switch
                  disabled={!isSuperUser}
                  id="scheduleEnabled"
                  sx={{ mt: 2.5 }}
                  checked={editProduct.scheduleEnabled ?? false}
                  onChange={handleEditProduct}
                />
              </FormControl>
            </Grid>
          </Grid>
        </FormSection>
        <FormSection defaultExpanded size="small" title={t("common:filtering")}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.blockedGroups")}</InputLabel>
                <Autocomplete
                  value={blockedGroups}
                  multiple
                  onChange={(e, value) => setBlockedGroups(value)}
                  options={groups}
                  getOptionLabel={(option) => option.group?.name || "-"}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.objectClasses")}</InputLabel>
                <Autocomplete
                  disabled={!isSuperUser}
                  value={editProduct.objectClasses ?? []}
                  multiple
                  onChange={(e, v) => handleEditProduct({ target: { value: v, id: "objectClasses" } })}
                  options={Object.keys(ObjectClass)}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.objectLayersToMatch")}</InputLabel>
                <Autocomplete
                  disabled={!isSuperUser}
                  value={editProduct.layers ?? []}
                  multiple
                  onChange={(e, v) => handleEditProduct({ target: { value: v, id: "layers" } })}
                  options={Object.keys(ObjectLayer)}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.objectTypesToMatch")}</InputLabel>
                <Autocomplete
                  disabled={!isSuperUser}
                  value={editProduct.objectTypes ?? []}
                  multiple
                  onChange={(e, v) => handleEditProduct({ target: { value: v, id: "objectTypes" } })}
                  options={Object.keys(ObjectType)}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.objectStatusToMatch")}</InputLabel>
                <Autocomplete
                  disabled={!isSuperUser}
                  value={editProduct.assetStatuses ?? []}
                  multiple
                  onChange={(e, v) => handleEditProduct({ target: { value: v, id: "assetStatuses" } })}
                  options={Object.keys(ObjectStatus)}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink>{t("field:label.aclToAllowProductAccess")}</InputLabel>
                <Autocomplete
                  disabled={!isSuperUser}
                  value={editProduct.acl ?? ""}
                  onChange={(e, v) => handleEditProduct({ target: { value: v, id: "acl" } })}
                  options={Object.keys(AclTypes)}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid mt={1} item xs={12}>
              <Divider orientation="horizontal" />
            </Grid>
            <Grid item xs={12}>
              <FormControl sx={{ width: "100%", mb: 1 }}>
                <InputLabel shrink>{t("field:label.metadataMatcher")}</InputLabel>
                <Autocomplete
                  value={[]}
                  multiple
                  onChange={(e, value) => updateMetaFilters(value[0])}
                  options={metadataFromStorage}
                  getOptionLabel={(option) => option.tagName || ""}
                  sx={{ width: "100%", mt: 3 }}
                  renderInput={(params) => (
                    <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("common:selectAnOption")} />
                  )}
                />
              </FormControl>

              {metadataFilters &&
                metadataFilters.map((x) => (
                  <Grid container spacing={0}>
                    <Grid item md={4}>
                      <Typography variant="body1" fontWeight={500} sx={{ color: "text.tier", mt: 1 }}>
                        {metadataFromStorage.find((y) => y.id === x.fieldId)?.tagName}
                      </Typography>
                    </Grid>
                    <Grid item md={2.5}>
                      <Typography variant="body1" fontWeight={500} sx={{ color: "text.tier", mt: 1 }}>
                        {metadataFromStorage.find((y) => y.id === x.fieldId)?.objectType}
                      </Typography>
                    </Grid>

                    <Grid item md={4.5}>
                      <OutlinedInput
                        placeholder={t("field:placeholder.enterValueToCompareMetadata")}
                        fullWidth
                        value={x.value || ""}
                        onChange={(e) =>
                          setMetadataFilters(
                            produce((draft) => {
                              const index = draft.findIndex((i) => i.fieldId === x.fieldId);
                              if (index !== -1) draft[index].value = e.target.value;
                            })
                          )
                        }
                      />
                    </Grid>
                    <Grid item md={1}>
                      <IconButton
                        sx={{ float: "right", mt: 0.5 }}
                        size="small"
                        onClick={() =>
                          setMetadataFilters(
                            produce((draft) => {
                              const index = draft.findIndex((f) => f.fieldId === x.fieldId);
                              if (index !== -1) draft.splice(index, 1);
                            })
                          )
                        }
                      >
                        <ClearIcon color="error" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          </Grid>
        </FormSection>
      </Stack>
    </Modal>
  );
});

export default ModalEditProduct;
